/**
 * INSi constants: display labels, OIDs, error-code → i18n key map, and the
 * homologation scenario id list. See tech_design_spec §8 / §9 and handoff §4 / §5.
 */
import { InsType } from './types';

/**
 * INS type → user-facing display label (e.g. "INS-NIR"). This is the backend
 * enum `.value`, which is NOT what the GraphQL wire carries (the wire literal
 * is the bare name `NIR`). Use this only for presentation.
 */
export const INS_TYPE_LABEL: Record<InsType, string> = {
  NIR: 'INS-NIR',
  NIA: 'INS-NIA',
  NIR_TEST: 'INS-NIR-TEST',
  NIR_DEMO: 'INS-NIR-DEMO',
};

/**
 * INS type → OID. Derived server-side from the type; the backend already
 * returns `oid` on every `GqlInsIdentifier`, so prefer the response value.
 * This map is for any place that needs the OID without a fetched identifier.
 */
export const INS_TYPE_OID: Record<InsType, string> = {
  NIR: '1.2.250.1.213.1.4.8',
  NIA: '1.2.250.1.213.1.4.9',
  NIR_TEST: '1.2.250.1.213.1.4.10',
  NIR_DEMO: '1.2.250.1.213.1.4.11',
};

/**
 * INSi error `code` (lowercase snake_case wire value) → i18n key.
 * Covers every code in handoff §5. The i18n values live in the locale files
 * (seeded French per handoff, English equivalents, others fall back).
 */
export const INSI_ERROR_I18N_KEY: Record<string, string> = {
  insi_feature_disabled_on_workspace: 'insi_feature_disabled_on_workspace',
  insi_search_invalid_auth_method: 'insi_search_invalid_auth_method',
  insi_traits_invalid_format: 'insi_traits_invalid_format',
  insi_search_technical_error: 'insi_search_technical_error',
  insi_duplicate_in_workspace: 'insi_duplicate_in_workspace',
  insi_attestation_without_ins: 'insi_attestation_without_ins',
  insi_qualify_no_ins: 'insi_qualify_no_ins',
  insi_qualify_invalid_status: 'insi_qualify_invalid_status',
  insi_homologation_unknown_scenario: 'insi_homologation_unknown_scenario',
};

/**
 * Homologation / CNDA scenario ids passed via the `X-INSi-Test-Scenario` header
 * (the backend selects the CNDA test fixture from it). Note: TEST_2.07_cas1 /
 * TEST_2.07_cas2 require the search input to contain at least 2 prénoms — they
 * exercise the multi-prénom iteration; we don't enforce it, the PS just enters
 * 2+ prénoms when running them.
 */
export const HOMOLOGATION_SCENARIO_IDS: string[] = [
  'TEST_2.03',
  'TEST_2.04_cas2',
  'TEST_2.05',
  'TEST_2.06',
  'TEST_2.07_cas1',
  'TEST_2.07_cas2',
  'TEST_2.08_cas1',
  'TEST_2.08_cas2',
];

/** What each scenario exercises — shown beside the id in the homologation banner. */
export const HOMOLOGATION_SCENARIO_LABELS: Record<string, string> = {
  'TEST_2.03': 'Trait format validation',
  'TEST_2.04_cas2': 'CR00 with historical INS entries',
  'TEST_2.05': 'CR00 returning a NIA',
  'TEST_2.06': 'Ambiguous match (CR02)',
  'TEST_2.07_cas1': 'Multiple first names — first prénom matches',
  'TEST_2.07_cas2': 'Multiple first names — no match (CR01)',
  'TEST_2.08_cas1': 'Secondary regime SNGI case 1',
  'TEST_2.08_cas2': 'Secondary regime SNGI case 2',
};

/** Homologation HTTP header names (headers, never GraphQL body — handoff §7). */
export const INSI_TEST_SCENARIO_HEADER = 'X-INSi-Test-Scenario';
export const INSI_CAPTURE_HEADER = 'X-INSi-Capture';
